import { Injectable, OnDestroy } from '@angular/core';
import { SocketService } from 'app/services/socket.service';
import { WebApisService } from 'app/services/web-apis.service';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { Ticker } from 'app/interfaces/ticker';
import { AuthenticationService } from 'app/auth/service/authentication.service';
import { map, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class StockService implements OnDestroy {

  public backBtnClass = 'btn btn-sm btn-outline-primary';
  public nextBtnClass = 'btn btn-sm btn-primary btn-next';
  public selectedTickerInfo = new BehaviorSubject<Ticker>(null);
  public shareTicker = this.selectedTickerInfo.asObservable();
  private _unsubscribeAll: Subject<any>;
  private userID: string = '';
  private zone_date = moment(new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })).format('YYYY-MM-DD');
  public tickersList = new BehaviorSubject<Ticker[]>(null);
  public tickersListObservable = this.tickersList.asObservable();
  constructor(
    private webApi: WebApisService,
    private socket: SocketService,
    private authService: AuthenticationService
  ) {
    this._unsubscribeAll = new Subject();
    this.initSocket();
    this.authService.currentUser.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: any) => {
      if (new Object(user).hasOwnProperty('user')) {
        this.userID = user.user.id;
      }
    })
  }

  ngOnDestroy(): void {

  }

  initSocket() {
    this.socket.on("connect", () => {
    });

    this.socket.on("close", () => {
    });

    this.socket.on("error", () => {
      console.log("socket error");
    });
  }

  getSocketTicker() {
    return this.socket.fromEvent('ticker-updates');
  }

  subcribeSocket() {
    this.socket.emit('subscribe', 'ticker-updates');
  }

  unsubcribeSocket() {
    this.socket.emit('unsubscribe', 'ticker-updates');
  }

  setTicker(row: Ticker) {
    row.price = Number(row.price.toFixed(2));
    row.percentageChange = Number((((Number(row.price) / Number(row.previousClose)) - 1) * 100).toFixed(2))
    this.selectedTickerInfo.next(row);
  }

  getAllTickers() {
    return this.webApi.get(`/ticker/bulk-download`);
  }

  getStockPagination(pageNumber: number, tickerPerPage: number, searchTerm: string) {
    let offset = tickerPerPage * (pageNumber - 1);
    if (searchTerm) {
      return this.webApi.get(`/ticker/search/${searchTerm}?limit=${tickerPerPage}&offset=${offset}`);
    } else {
      return this.webApi.get(`/ticker?limit=${tickerPerPage}&offset=${offset}`);
    }
  }

  getTickerByName(tickername) {
    return this.webApi.get<any>(`/ticker/${tickername}`);
  }

  getTickerTodayData(tickername) {
    return this.webApi.get<any>(`/ticker/${tickername}/aggregatedBars`);
  }

  getNews(tickername: string) {
    return this.webApi.get(`/news/${tickername}`);
  }

  getSocialSentimentTrends(pagename, tickername, limit?, offset?) {
    return this.webApi.get<any>(`/${pagename}?ticker=${tickername}&limit=${limit}&offset=${offset}`);
  }

  getSocialSentiment(page_name) {
    if (this.userID) {
      return this.webApi.get<any>(`/socialsentiment/${page_name}/user/${this.userID}`).pipe(
        map(actions => {
          return actions.data;
        })
      );
    } else {
      return of([]);
    }
  }

  deleteSocialSentiment(obj_id) {
    return this.webApi.delete<any>(`/socialsentiment/${obj_id}`, null);
  }

  getUserNews() {
    if (this.userID) {
      return this.webApi.get(`/news/user/${this.userID}`);
    } else {
      return of([]);
    }
  }

  addSocialSentiment(data: any) {
    data.UserID = this.userID;
    return this.webApi.post<any>(`/socialsentiment`, data);
  }

  deleteUserNews(news_id) {
    if (this.userID) {
      return this.webApi.delete(`/news/user/${this.userID}/${news_id}`, {});
    } else {
      return of([]);
    }
  }

  getTickerSocialsentiment(data: Ticker) {
    return this.webApi.get<any>(`/ticker/${data.ticker}/socialsentiment?recordedOn=${this.zone_date}`);
  }

  getTickerHistoricalData() {
    return this.webApi.get<any>(`/ticker/${this.selectedTickerInfo.value.ticker.toLowerCase()}/socialsentiment/history`);
  }

  getTickerTechnical(data: Ticker) {
    return this.webApi.get<any>(`/ticker/${data.ticker}/technical`);
  }

  getTickerAlternates(data: Ticker) {
    return this.webApi.get<any>(`/ticker/${data.ticker}/alternates`);
  }

  getTickerValuationAndOperation(ticker) {
    return this.webApi.get<any>(`/ticker/${ticker}/valuationandoperation`);
  }

  getPortfolioTickers() {
    return this.webApi.get<any>(`/portfolio/user/${this.userID}/ticker`);
  }

  deletePortfolioTicker(ticker) {
    return this.webApi.delete<any>(`/portfolio/user/${this.userID}/ticker/${ticker}`, null);
  }

  addPortfolio(data) {
    return this.webApi.post<any>(`/portfolio/user/${this.userID}/bulk`, data);
  }

  deleteWishlistTicker(ticker) {
    return this.webApi.delete<any>(`/watchlist/user/${this.userID}/ticker/${ticker}`, null);
  }

  getWishlistTickers() {
    return this.webApi.get<any>(`/watchlist/user/${this.userID}/ticker`);
  }

  addWishlist(data) {
    return this.webApi.post<any>(`/watchlist/user/${this.userID}/bulk`, data);
  }

  getTrend() {
    return this.webApi.get<any>(`/trend`);
  }

  getTickerMacro() {
    return this.webApi.get<any>(`/macro`);
  }

  getAllTickerList() {
    return this.webApi.get<any>(`/stock/landing/limited/ticker`).pipe(
      map(actions => {
        return actions.data
      })
    );
  }

  getIndustryMap() {
    return this.webApi.get<any>(`/industryMap`);
  }

  getYieldCurve() {
    return this.webApi.get<any>(`/macro/yieldCurve`);
  }

  getFedRatesHike() {
    return this.webApi.get<any>(`/fedRatesHike`);
  }

  searchTickers(search) {
    return this.webApi.get<any>(`/ticker/search/${search}?limit=${10}&offset=${0}`);
  }

  getCurrentAtPortfolio() {
    return this.webApi.get<any>(`/stock/atportfolio/current`);
  }

  getResultsAtPortfolio(date) {
    return this.webApi.get<any>(`/stock/atportfolio/results?date=${date}`);
  }

  getLastThreeATPortfolio(date) {
    return this.webApi.get<any>(`/stock/atportfolio/lastThree?date=${date}`);
  }

  /* GET AT PORTFOLIO DATES */
  getATDates() {
    return this.webApi.get<any>(`/stock/atportfolio/dates`);
  }
}
